import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { config } from '../../../config';
import { useReactToPrint } from 'react-to-print';
import InvoiceTemplate from './InvoiceTemplate';
import CustomerTable from './Component/CustomerTable';
import SearchAndFilter from './Component/SearchAndFilter';
import UpdateCustomer from './UpdateCustomer';
import SalesReportModal from './Component/SalesReportModal';
import CartProductDetails from './Component/CartProductDetails';

const apiUrl = config.apiUrl;
const steadfastApiUrl = 'https://portal.packzy.com/api/v1';

const ViewCustomer = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingApplication, setEditingApplication] = useState(null);
  const [formData, setFormData] = useState({
    order_id: '',
    customer_name: '',
    customer_address: '',
    phone_number: '',
    product_name: '',
    color: '',
    size: '',
    quantity: '',
    total: '',
    delivery_status: '',
    delivery_note: '',
  });
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [steadfast, setSteadfast] = useState({});
  const [orderStatuses, setOrderStatuses] = useState({});
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [printingInvoice, setPrintingInvoice] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showStatus, setShowStatus] = useState({});
  const [fraudDetails, setFraudDetails] = useState({});
  const itemsPerPage = 10;
  const invoiceRef = useRef();
  const statusCache = useRef({});

  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState(null);
  
  // Date range filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateFilterApplied, setDateFilterApplied] = useState(false);
  const [salesReport, setSalesReport] = useState(null);
  const [showSalesReport, setShowSalesReport] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);

useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      const [customersRes, steadfastRes] = await Promise.all([
        axios.get(`${apiUrl}/customers`),
        axios.get(`${apiUrl}/steadfasts`)
      ]);
      
      setSteadfast(steadfastRes.data[0] || {});
      let customers = customersRes.data.customers || customersRes.data;
      
      // Sort by newest first
      customers = customers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setApplications(customers);
      setFilteredApplications(customers);
      setTotalPages(Math.ceil(customers.length / itemsPerPage));
      
      setLoading(false);
    } catch (err) {
      console.error("API Error:", err.response ? err.response.data : err.message);
      setError("Failed to load data. Please try again.");
      setLoading(false);
    }
  };
  
  fetchData();
}, []);

const handleImageClick = (item) => {
  setSelectedProductDetails({
    ...item,
    images: item.product_image || item.colors?.map(c => c.image) || []
  });
  setShowProductModal(true);
};

  const handleSearch = async () => {
    const query = searchQuery.trim();
    
    if (!query) {
      setSearchError('Please enter a search term (name, phone or address)');
      return;
    }

    try {
      setLoading(true);
      setSearchError(null);
      
      const response = await axios.get(`${apiUrl}/customers/search/customerdata`, {
        params: {
          search: query
        }
      });

      if (response.data.customers && response.data.customers.length > 0) {
        setApplications(response.data.customers);
        setFilteredApplications(response.data.customers);
        setTotalPages(Math.ceil(response.data.customers.length / itemsPerPage));
        setCurrentPage(1);
      } else {
        setSearchError('No customers found with this search criteria');
        setApplications([]);
        setFilteredApplications([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Search error:", err);
      setSearchError(err.response?.data?.message || 'Error performing search');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSearch = async () => {
    setSearchQuery('');
    setSearchError(null);
    
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/customers`);
      const customers = response.data.customers || response.data;
      const filteredCustomers = customers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setApplications(customers);
      setFilteredApplications(filteredCustomers);
      setTotalPages(Math.ceil(customers.length / itemsPerPage));
      setCurrentPage(1);
    } catch (err) {
      console.error("Error resetting search:", err);
      setError("Failed to reset search. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        await axios.delete(`${apiUrl}/customerdelete/${id}`);
        setApplications(prev => prev.filter(app => app.id !== id));
        setSelectedApplications(prev => prev.filter(appId => appId !== id));
      } catch (err) {
        console.error("Error deleting order:", err?.response?.data?.message);
        setError("Failed to delete order. Please try again.");
      }
    }
  };

  // Filter applications by date range
  const filterByDateRange = (apps) => {
    if (!startDate || !endDate) return apps;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999); // Include entire end date
    
    return apps.filter(app => {
      const appDate = new Date(app.created_at);
      return appDate >= start && appDate <= end;
    });
  };

  // Generate sales report for the selected date range
  const generateSalesReport = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }
    
    const filteredOrders = filterByDateRange(applications);
    
    const report = {
      totalOrders: filteredOrders.length,
      totalSales: filteredOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0),
      deliveredOrders: filteredOrders.filter(order => 
        order.delivery_status?.toLowerCase() === 'delivered').length,
      cancelledOrders: filteredOrders.filter(order => 
        order.delivery_status?.toLowerCase() === 'cancelled').length,
      pendingOrders: filteredOrders.filter(order => 
        !order.delivery_status || 
        order.delivery_status.toLowerCase() === 'not confirm' ||
        order.delivery_status.toLowerCase() === 'confirmed').length,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      orders: filteredOrders
    };
    
    setSalesReport(report);
    setShowSalesReport(true);
  };

  // Apply date filter
  const applyDateFilter = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }
    
    setDateFilterApplied(true);
    const filtered = filterByDateRange(applications);
    setFilteredApplications(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentPage(1);
  };

  // Fetch initial data with newest orders first
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [customersRes, steadfastRes] = await Promise.all([
          axios.get(`${apiUrl}/customers`),
          axios.get(`${apiUrl}/steadfasts`)
        ]);
        
        setSteadfast(steadfastRes.data[0] || {});
        let customers = customersRes.data.customers || customersRes.data;
        
        // Sort by newest first
        customers = customers.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        
        setApplications(customers);
        
      // Calculate total pages
      const filtered = filterApplications(customers);
        setTotalPages(Math.ceil(filtered.length / itemsPerPage));
        
        setLoading(false);
      } catch (err) {
        console.error("API Error:", err.response ? err.response.data : err.message);
        setError("Failed to load data. Please try again.");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filterApplications = (apps) => {
    switch (activeTab) {
      case 'not_confirm':
        return apps.filter(app => 
          (!app.delivery_status || app.delivery_status === 'Not Confirm'));
      case 'confirmed':
        return apps.filter(app => 
          app.delivery_status && 
          app.delivery_status.toLowerCase() === 'confirmed'
        );
      case 'delivered':
        return apps.filter(app => 
          (orderStatuses[app.order_id] && 
          orderStatuses[app.order_id].toLowerCase() === 'delivered') || 
          (app.delivery_status && 
          app.delivery_status.toLowerCase() === 'delivered')
        );
      case 'cancelled':
        return apps.filter(app => 
          app.delivery_status && 
          app.delivery_status.toLowerCase() === 'cancelled'
        );
      case 'return':
        return apps.filter(app => 
          app.delivery_status && 
          app.delivery_status.toLowerCase() === 'return'
        );
      case 'hold':
        return apps.filter(app => 
          app.delivery_status && 
          app.delivery_status.toLowerCase() === 'hold'
        );
      case 'blocked':
        return apps.filter(app => 
          app.delivery_status && 
          app.delivery_status.toLowerCase() === 'blocked'
        );
      default:
        return apps;
    }
  };

  // Reset date filter
  const resetDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setDateFilterApplied(false);
    setFilteredApplications(filterApplications(applications));
    setTotalPages(Math.ceil(applications.length / itemsPerPage));
    setCurrentPage(1);
  };

  // Filter applications and handle pagination
  useEffect(() => {
    if (applications.length === 0) return;

    const filtered = filterApplications(applications);
    setFilteredApplications(filtered);
    
    // Update total pages when filtered results change
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    
  }, [activeTab, applications, orderStatuses]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddPoints = async (customerId) => {
    try {
      const response = await axios.post(`${apiUrl}/customers/${customerId}/add-points`);
      
      alert(`পয়েন্ট যোগ করা হয়েছে! যোগ হয়েছে: ${response.data.points_added}, মোট পয়েন্ট: ${response.data.total_points}`);
      
      const customersRes = await axios.get(`${apiUrl}/customers`);
      let customers = customersRes.data.customers || customersRes.data;
      customers = customers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setApplications(customers);
      
    } catch (err) {
      console.error("Error adding points:", err);
      setError(err.response?.data?.message || "Failed to add points.");
    }
  };

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredApplications.slice(startIndex, endIndex);
  };


  const handleSelectAllChange = () => {
    const currentItems = getCurrentPageItems();
    if (selectAll) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(currentItems.map(app => app.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSubmitToSteadfast = async () => {
    if (selectedApplications.length === 0) {
      alert("অন্তত একটি অর্ডার সিলেক্ট করুন");
      return;
    }

    const selectedOrders = applications
      .filter(app => selectedApplications.includes(app.id))
      .map(order => ({
        invoice: order.order_id,
        recipient_name: order.customer_name,
        recipient_address: order.customer_address,
        recipient_phone: order.phone_number,
        cod_amount: Number(order.total),
        note: order.delivery_note,
      }));

    try {
      const response = await axios.post(
        `${steadfastApiUrl}/create_order/bulk-order`, 
        selectedOrders,
        {
          headers: {
            'Api-Key': steadfast.apiKey,
            'Secret-Key': steadfast.secretKey,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      // consignment_id ডাটাবেজে আপডেট করা
      try {
        await Promise.all(
          response.data.data.map(async (orderResponse) => {
            await axios.put(`${apiUrl}/customersconsignmentupdate/${orderResponse.invoice}`, {
              consignment_id: orderResponse.consignment_id,
              delivery_status: 'confirmed',
            });
          })
        );
        // সফলতার মেসেজ দেখানো
      alert(`সফলভাবে ${response.data.data.length}টি অর্ডার Steadfast-এ সাবমিট করা হয়েছে এবং ডাটাবেস আপডেট করা হয়েছে!`);
      } catch (error) {
        console.error(error.response?.data?.message);
        alert("Consignment ID ডাটাবেজে আপডেট করতে সমস্যা হয়েছে।");
      }

      
      // try {
      //   // Update each selected order in the database
      //   await Promise.all(selectedApplications.map(async (id) => {
      //     const order = applications.find(app => app.id === id);
      //     if (order) {
      //       await axios.put(`${apiUrl}/customersupdate/${id}`, {
      //         ...order,
      //         delivery_status: 'confirmed'
      //       });
      //     }
      //   }));

      //   // Update local state
      //   const updatedApplications = applications.map(app => {
      //     if (selectedApplications.includes(app.id)) {
      //       return {
      //         ...app,
      //         delivery_status: 'confirmed'
      //       };
      //     }
      //     return app;
      //   });

      //   setApplications(updatedApplications);
        
      //   // Update statuses in state
      //   const newStatuses = {};
      //   selectedApplications.forEach(id => {
      //     const order = applications.find(app => app.id === id);
      //     if (order) {
      //       newStatuses[order.order_id] = 'confirmed';
      //     }
      //   });
        
      //   setOrderStatuses(prev => ({ ...prev, ...newStatuses }));

      //   alert("অর্ডার সফলভাবে সাবমিট হয়েছে এবং ডাটাবেস আপডেট করা হয়েছে!");

      // } catch (dbError) {
      //   console.error("ডাটাবেস আপডেট করতে সমস্যা:", dbError);
      //   alert("অর্ডার সাবমিট হয়েছে কিন্তু ডাটাবেস আপডেট করতে সমস্যা হয়েছে। পরে আবার চেষ্টা করুন।");
      // }

    } catch (err) {
      console.error("স্ট্যাডফাস্টে সাবমিট করার সময় এরর:", err);
      let errorMessage = "অর্ডার সাবমিট করতে সমস্যা হয়েছে";
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = "API Key বা Secret Key ভুল";
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      }
      alert(errorMessage);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch(status.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'delivered':
      case 'delivered_approval_pending':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
      case 'cancelled_approval_pending':
        return 'bg-red-100 text-red-800';
      case 'fraud':
        return 'bg-red-100 text-red-800';
      case 'error':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  // Fetch status for a single order with retry logic
  const fetchOrderStatus = async (invoice, retries = 3) => {
    try {
      const response = await axios.get(`${steadfastApiUrl}/status_by_invoice/${invoice}`, {
        headers: {
          'Api-Key': steadfast.apiKey,
          'Secret-Key': steadfast.secretKey,
          'Content-Type': 'application/json'
        },
        timeout: 5000
      });
      
      const status = response.data.delivery_status || 'Not Confirm';
      // Update cache
      statusCache.current[invoice] = status;
      return status;
    } catch (err) {
      if (err.response?.status === 429 && retries > 0) {
        // Exponential backoff
        const delay = Math.pow(2, 4 - retries) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchOrderStatus(invoice, retries - 1);
      }
      console.error(`Error fetching status for invoice ${invoice}:`, err);
      return 'Not Confirm';
    }
  };

  // Refresh all statuses for current page items
  const refreshAllStatuses = async () => {
    try {
      setRefreshLoading(true);
      const currentItems = getCurrentPageItems();
      const statusMap = {};
      
      for (const item of currentItems) {
        const status = await fetchOrderStatus(item.order_id);
        statusMap[item.order_id] = status;
      }
      
      setOrderStatuses(prev => ({ ...prev, ...statusMap }));
      setRefreshLoading(false);
    } catch (err) {
      console.error("Error refreshing statuses:", err);
      setError("Failed to refresh statuses. Please try again.");
      setRefreshLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handleCheckboxChange = (id) => {
    setSelectedApplications(prev =>
      prev.includes(id)
        ? prev.filter(appId => appId !== id)
        : [...prev, id]
    );
  };

  const handleEditClick = (app) => {
    setEditingApplication(app.id);
    setFormData({
      order_id: app.order_id || '',
      customer_name: app.customer_name || '',
      customer_address: app.customer_address || '',
      phone_number: app.phone_number || '',
      product_name: app.product_name || '',
      color: app.color || '',
      size: app.size || '',
      quantity: app.quantity || '',
      total: app.total || '',
      delivery_status: app.delivery_status || '',
      delivery_note: app.delivery_note || '',
    });
  };

  // Fetch status for an order when clicked
  const fetchStatusOnClick = async (orderId) => {
    const order = applications.find(app => app.id === orderId);
    if (!order) return;

    try {
      setShowStatus(prev => ({ ...prev, [orderId]: 'loading' }));
      
      const status = await fetchOrderStatus(order.order_id);
      
      setOrderStatuses(prev => ({
        ...prev,
        [order.order_id]: status
      }));
      
      setShowStatus(prev => ({ ...prev, [orderId]: status }));
    } catch (err) {
      console.error("Error fetching status:", err);
      setShowStatus(prev => ({ ...prev, [orderId]: 'error' }));
    }
  };

  // Check fraud details for a customer
    const checkFraudDetails = async (phoneNumber) => {
      try {
        setFraudDetails(prev => ({ ...prev, [phoneNumber]: { loading: true } }));
        
        const response = await axios.get(`${steadfastApiUrl}/fraud_check/${phoneNumber}`, {
          headers: {
            'Api-Key': steadfast.apiKey,
            'Secret-Key': steadfast.secretKey,
            'Content-Type': 'application/json'
          },
          timeout: 5000
        });
        
        setFraudDetails(prev => ({
          ...prev,
          [phoneNumber]: {
            loading: false,
            data: response.data,
            isFraud: response.data.total_fraud_reports.length > 0
          }
        }));
      } catch (err) {
        console.error("Error checking fraud details:", err);
        setFraudDetails(prev => ({
          ...prev,
          [phoneNumber]: {
            loading: false,
            error: 'Error fetching fraud details'
          }
        }));
      }
    };



  // Filter applications by date range
  const handlePrint = useReactToPrint({
    content: () => invoiceRef.current,
  });

 const handleStatusUpdate = async (orderId, newStatus) => {
  try {
    const order = applications.find(app => app.id === orderId);
    if (!order) return;

    const response = await axios.put(`${apiUrl}/customersupdate/${orderId}`, {
      ...order,
      delivery_status: newStatus
    });

      // লোকাল স্টেট আপডেট করুন
      setApplications(prev => 
        prev.map(app => 
          app.id === orderId ? { ...app, delivery_status: newStatus } : app
        )
      );

      // অর্ডার স্ট্যাটাস স্টেট আপডেট করুন
      setOrderStatuses(prev => ({
        ...prev,
        [order.order_id]: newStatus
      }));

      alert(`Status updated to ${newStatus}`);
      
    } catch (err) {
      console.error("Error updating status:", err?.response?.data?.message);
      setError("Failed to update status. Please try again.");
    }
  };

  const handlePrintClick = (app) => {
    setPrintingInvoice(app);
    setShowPrintPreview(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${apiUrl}/customersupdate/${editingApplication}`, formData);
      setApplications(prev =>
        prev.map(app =>
          app.id === editingApplication ? { ...app, ...formData } : app
        )
      );
      setEditingApplication(null);
    } catch (err) {
      console.error("Error updating order:", err);
      setError(err.response?.data?.message || "Failed to update order.");
    }
  };

  const currentItems = getCurrentPageItems();

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <CartProductDetails 
        showProductModal={showProductModal}
        selectedProductDetails={selectedProductDetails}
        setShowProductModal={setShowProductModal}
      />
      <SearchAndFilter
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSearch={handleSearch}
        onResetSearch={handleResetSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        onApplyDateFilter={applyDateFilter}
        onGenerateReport={generateSalesReport}
        onResetDateFilter={resetDateFilter}
        dateFilterApplied={dateFilterApplied}
        searchError={searchError}
      />

      <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">Customer Orders</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <button
              onClick={refreshAllStatuses}
              disabled={refreshLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm md:text-base"
            >
              {refreshLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                <>
                  <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  Refresh Statuses
                </>
              )}
            </button>
            <button
              onClick={handleSubmitToSteadfast}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center text-sm md:text-base"
              disabled={selectedApplications.length === 0}
            >
              <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
              </svg>
              Submit to Steadfast ({selectedApplications.length})
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200 overflow-x-auto">
          <nav className="-mb-px flex space-x-4 whitespace-nowrap">
            {['all', 'not_confirm', 'confirmed', 'delivered', 'return', 'cancelled', 'hold', 'blocked'].map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
                className={`py-3 px-1 border-b-2 font-medium text-xs sm:text-sm ${activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                {tab.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </button>
            ))}
          </nav>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg border border-red-100">
            {error}
            <button 
              onClick={() => setError(null)} 
              className="float-right text-red-800 font-bold"
            >
              ×
            </button>
          </div>
        )}

        

        <CustomerTable
          applications={applications}
          currentItems={getCurrentPageItems()}
          selectedApplications={selectedApplications}
          handleCheckboxChange={handleCheckboxChange}
          handleEditClick={handleEditClick}
          handlePrintClick={handlePrintClick}
          handleDeleteClick={handleDelete}
          handleAddPoints={handleAddPoints}
          getStatusColor={getStatusColor}
          showStatus={showStatus}
          orderStatuses={orderStatuses}
          fetchStatusOnClick={fetchStatusOnClick}
          onUpdateStatus={handleStatusUpdate} 
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          activeTab={activeTab}
          selectAll={selectAll}
          handleSelectAllChange={handleSelectAllChange}
          fraudDetails={fraudDetails}
          handleImageClick={handleImageClick}
          checkFraudDetails={checkFraudDetails}
        />
      </div>

      <SalesReportModal
        isOpen={showSalesReport}
        onClose={() => setShowSalesReport(false)}
        report={salesReport}
        onExportCSV={() => {
          const csvContent = [
            ['Order ID', 'Date', 'Customer', 'Phone', 'Amount', 'Status'],
            ...salesReport.orders.map(order => [
              order.order_id,
              new Date(order.created_at).toLocaleDateString(),
              order.customer_name,
              order.phone_number,
              order.total,
              order.delivery_status || 'Not Confirm'
            ])
          ].map(e => e.join(",")).join("\n");

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement("a");
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", `sales_report_${salesReport.startDate}_to_${salesReport.endDate}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
      />

      {editingApplication && (
        <UpdateCustomer
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          loading={loading}
          error={error}
          setEditingApplication={setEditingApplication}
        />
      )}

      {showPrintPreview && printingInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">Invoice Preview</h3>
              <div className="space-x-2">
                <button 
                  onClick={handlePrint}
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  Print
                </button>
                <button 
                  onClick={() => setShowPrintPreview(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="p-6">
              <InvoiceTemplate
                ref={invoiceRef} 
                order={printingInvoice} 
                logoUrl="/images/logo.png" 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCustomer;
































// import React, { useEffect, useState, useRef } from "react";
// import axios from "axios";
// import { config } from "../../../config";
// import UpdateCustomer from "./UpdateCustomer";
// import { useReactToPrint } from 'react-to-print';
// import InvoiceTemplate from "./InvoiceTemplate";
// import { FaCheck, FaPauseCircle, FaUndo, FaPlusCircle, FaTimesCircle, FaCheckCircle, FaMinusCircle, FaExclamationTriangle, FaEye, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";

// const apiUrl = config.apiUrl;
// const imageUrl = config.imageUrl;
// const steadfastApiUrl = 'https://portal.packzy.com/api/v1';

// const ViewCustomer = () => {
//   const [applications, setApplications] = useState([]);
//   const [filteredApplications, setFilteredApplications] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [editingApplication, setEditingApplication] = useState(null);
//   const [formData, setFormData] = useState({
//     order_id: '',
//     customer_name: '',
//     customer_address: '',
//     phone_number: '',
//     product_name: '',
//     color: '',
//     size: '',
//     quantity: '',
//     total: '',
//     delivery_status: '',
//     delivery_note: '',
//   });
//   const [selectedApplications, setSelectedApplications] = useState([]);
//   const [selectAll, setSelectAll] = useState(false);
//   const [steadfast, setSteadfast] = useState({});
//   const [orderStatuses, setOrderStatuses] = useState({});
//   const [refreshLoading, setRefreshLoading] = useState(false);
//   const [activeTab, setActiveTab] = useState('all');
//   const [printingInvoice, setPrintingInvoice] = useState(null);
//   const [showPrintPreview, setShowPrintPreview] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [showStatus, setShowStatus] = useState({});
//   const [fraudDetails, setFraudDetails] = useState({});
//   const itemsPerPage = 10;
//   const invoiceRef = useRef();
//   const statusCache = useRef({});

//   const [searchQuery, setSearchQuery] = useState('');
//   const [searchError, setSearchError] = useState(null);
//   const [selectedProductDetails, setSelectedProductDetails] = useState(null);
//   const [showProductModal, setShowProductModal] = useState(false);
  
//   // Date range filter states
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [dateFilterApplied, setDateFilterApplied] = useState(false);
//   const [salesReport, setSalesReport] = useState(null);
//   const [showSalesReport, setShowSalesReport] = useState(false);

//   const handleImageClick = (item) => {
//     setSelectedProductDetails(item);
//     setShowProductModal(true);
//   };



//   const updateOrderStatus = async (orderId, status) => {
//     try {
//       const order = applications.find(app => app.id === orderId);
      
//       if (!order) {
//         throw new Error("Order not found");
//       }
  
//       const payload = {
//         ...order,
//         delivery_status: status,
//         id: orderId
//       };
  
//       const response = await axios.put(`${apiUrl}/customersupdate/${orderId}`, payload);
      
//       setApplications(prev =>
//         prev.map(app =>
//           app.id === orderId ? { ...app, delivery_status: status } : app
//         )
//       );
      
//       setOrderStatuses(prev => ({
//         ...prev,
//         [order.order_id]: status
//       }));
      
//       // Update cache
//       statusCache.current[order.order_id] = status;
      
//       alert(`Order status updated to ${status}!`);
//     } catch (err) {
//       console.error("Error updating status:", err?.response?.data);
//       setError(err?.response?.data?.message || "Failed to update status");
//     }
//   };

//   // Fetch initial data with newest orders first
//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
//         const [customersRes, steadfastRes] = await Promise.all([
//           axios.get(`${apiUrl}/customers`),
//           axios.get(`${apiUrl}/steadfasts`)
//         ]);
        
//         setSteadfast(steadfastRes.data[0] || {});
//         let customers = customersRes.data.customers || customersRes.data;
        
//         // Sort by newest first
//         customers = customers.sort((a, b) => 
//           new Date(b.created_at) - new Date(a.created_at)
//         );
        
//         setApplications(customers);
        
//         // Calculate total pages
//         const filtered = filterApplications(customers);
//         setTotalPages(Math.ceil(filtered.length / itemsPerPage));
        
//         setLoading(false);
//       } catch (err) {
//         console.error("API Error:", err.response ? err.response.data : err.message);
//         setError("Failed to load data. Please try again.");
//         setLoading(false);
//       }
//     };
//     fetchData();
//   }, []);

//   // Filter applications by date range
//   const filterByDateRange = (apps) => {
//     if (!startDate || !endDate) return apps;
    
//     const start = new Date(startDate);
//     const end = new Date(endDate);
//     end.setHours(23, 59, 59, 999); // Include entire end date
    
//     return apps.filter(app => {
//       const appDate = new Date(app.created_at);
//       return appDate >= start && appDate <= end;
//     });
//   };

//   // Generate sales report for the selected date range
//   const generateSalesReport = () => {
//     if (!startDate || !endDate) {
//       alert("Please select both start and end dates");
//       return;
//     }
    
//     const filteredOrders = filterByDateRange(applications);
    
//     const report = {
//       totalOrders: filteredOrders.length,
//       totalSales: filteredOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0),
//       deliveredOrders: filteredOrders.filter(order => 
//         order.delivery_status?.toLowerCase() === 'delivered').length,
//       cancelledOrders: filteredOrders.filter(order => 
//         order.delivery_status?.toLowerCase() === 'cancelled').length,
//       pendingOrders: filteredOrders.filter(order => 
//         !order.delivery_status || 
//         order.delivery_status.toLowerCase() === 'not confirm' ||
//         order.delivery_status.toLowerCase() === 'confirmed').length,
//       startDate: startDate.toISOString().split('T')[0],
//       endDate: endDate.toISOString().split('T')[0],
//       orders: filteredOrders
//     };
    
//     setSalesReport(report);
//     setShowSalesReport(true);
//   };

//   // Apply date filter
//   const applyDateFilter = () => {
//     if (!startDate || !endDate) {
//       alert("Please select both start and end dates");
//       return;
//     }
    
//     setDateFilterApplied(true);
//     const filtered = filterByDateRange(applications);
//     setFilteredApplications(filtered);
//     setTotalPages(Math.ceil(filtered.length / itemsPerPage));
//     setCurrentPage(1);
//   };

//   // Reset date filter
//   const resetDateFilter = () => {
//     setStartDate(null);
//     setEndDate(null);
//     setDateFilterApplied(false);
//     setFilteredApplications(filterApplications(applications));
//     setTotalPages(Math.ceil(applications.length / itemsPerPage));
//     setCurrentPage(1);
//   };

//   // Filter applications and handle pagination
//   useEffect(() => {
//     if (applications.length === 0) return;

//     const filtered = filterApplications(applications);
//     setFilteredApplications(filtered);
    
//     // Update total pages when filtered results change
//     setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    
//   }, [activeTab, applications, orderStatuses]);

//   // Get current page items
//   const getCurrentPageItems = () => {
//     const startIndex = (currentPage - 1) * itemsPerPage;
//     const endIndex = startIndex + itemsPerPage;
//     return filteredApplications.slice(startIndex, endIndex);
//   };

//   const filterApplications = (apps) => {
//     switch (activeTab) {
//       case 'not_confirm':
//         return apps.filter(app => 
//           (!app.delivery_status || app.delivery_status === 'Not Confirm'));
//       case 'confirmed':
//         return apps.filter(app => 
//           app.delivery_status && 
//           app.delivery_status.toLowerCase() === 'confirmed'
//         );
//       case 'delivered':
//         return apps.filter(app => 
//           (orderStatuses[app.order_id] && 
//           orderStatuses[app.order_id].toLowerCase() === 'delivered') || 
//           (app.delivery_status && 
//           app.delivery_status.toLowerCase() === 'delivered')
//         );
//       case 'cancelled':
//         return apps.filter(app => 
//           app.delivery_status && 
//           app.delivery_status.toLowerCase() === 'cancelled'
//         );
//       case 'return':
//         return apps.filter(app => 
//           app.delivery_status && 
//           app.delivery_status.toLowerCase() === 'return'
//         );
//       case 'hold':
//         return apps.filter(app => 
//           app.delivery_status && 
//           app.delivery_status.toLowerCase() === 'hold'
//         );
//       case 'blocked':
//         return apps.filter(app => 
//           app.delivery_status && 
//           app.delivery_status.toLowerCase() === 'blocked'
//         );
//       default:
//         return apps;
//     }
//   };

//   // Fetch status for an order when clicked
//   const fetchStatusOnClick = async (orderId) => {
//     const order = applications.find(app => app.id === orderId);
//     if (!order) return;

//     try {
//       setShowStatus(prev => ({ ...prev, [orderId]: 'loading' }));
      
//       const status = await fetchOrderStatus(order.order_id);
      
//       setOrderStatuses(prev => ({
//         ...prev,
//         [order.order_id]: status
//       }));
      
//       setShowStatus(prev => ({ ...prev, [orderId]: status }));
//     } catch (err) {
//       console.error("Error fetching status:", err);
//       setShowStatus(prev => ({ ...prev, [orderId]: 'error' }));
//     }
//   };

//   // Check fraud details for a customer
//   const checkFraudDetails = async (phoneNumber) => {
//     try {
//       setFraudDetails(prev => ({ ...prev, [phoneNumber]: { loading: true } }));
      
//       const response = await axios.get(`${steadfastApiUrl}/fraud_check/${phoneNumber}`, {
//         headers: {
//           'Api-Key': steadfast.apiKey,
//           'Secret-Key': steadfast.secretKey,
//           'Content-Type': 'application/json'
//         },
//         timeout: 5000
//       });
      
//       setFraudDetails(prev => ({
//         ...prev,
//         [phoneNumber]: {
//           loading: false,
//           data: response.data,
//           isFraud: response.data.total_fraud_reports.length > 0
//         }
//       }));
//     } catch (err) {
//       console.error("Error checking fraud details:", err);
//       setFraudDetails(prev => ({
//         ...prev,
//         [phoneNumber]: {
//           loading: false,
//           error: 'Error fetching fraud details'
//         }
//       }));
//     }
//   };

//   // Fetch status for a single order with retry logic
//   const fetchOrderStatus = async (invoice, retries = 3) => {
//     try {
//       const response = await axios.get(`${steadfastApiUrl}/status_by_invoice/${invoice}`, {
//         headers: {
//           'Api-Key': steadfast.apiKey,
//           'Secret-Key': steadfast.secretKey,
//           'Content-Type': 'application/json'
//         },
//         timeout: 5000
//       });
      
//       const status = response.data.delivery_status || 'Not Confirm';
//       // Update cache
//       statusCache.current[invoice] = status;
//       return status;
//     } catch (err) {
//       if (err.response?.status === 429 && retries > 0) {
//         // Exponential backoff
//         const delay = Math.pow(2, 4 - retries) * 1000;
//         await new Promise(resolve => setTimeout(resolve, delay));
//         return fetchOrderStatus(invoice, retries - 1);
//       }
//       console.error(`Error fetching status for invoice ${invoice}:`, err);
//       return 'Not Confirm';
//     }
//   };

//   const handlePrint = useReactToPrint({
//     content: () => invoiceRef.current,
//   });

//   const handlePrintClick = (app) => {
//     setPrintingInvoice(app);
//     setShowPrintPreview(true);
//   };

//   // Refresh all statuses for current page items
//   const refreshAllStatuses = async () => {
//     try {
//       setRefreshLoading(true);
//       const currentItems = getCurrentPageItems();
//       const statusMap = {};
      
//       for (const item of currentItems) {
//         const status = await fetchOrderStatus(item.order_id);
//         statusMap[item.order_id] = status;
//       }
      
//       setOrderStatuses(prev => ({ ...prev, ...statusMap }));
//       setRefreshLoading(false);
//     } catch (err) {
//       console.error("Error refreshing statuses:", err);
//       setError("Failed to refresh statuses. Please try again.");
//       setRefreshLoading(false);
//     }
//   };

//   const handleSearch = async () => {
//     const query = searchQuery.trim();
    
//     if (!query) {
//       setSearchError('Please enter a search term (name, phone or address)');
//       return;
//     }

//     try {
//       setLoading(true);
//       setSearchError(null);
      
//       const response = await axios.get(`${apiUrl}/customers/search/customerdata`, {
//         params: {
//           search: query
//         }
//       });

//       if (response.data.customers && response.data.customers.length > 0) {
//         setApplications(response.data.customers);
//         setFilteredApplications(response.data.customers);
//         setTotalPages(Math.ceil(response.data.customers.length / itemsPerPage));
//         setCurrentPage(1);
//       } else {
//         setSearchError('No customers found with this search criteria');
//         setApplications([]);
//         setFilteredApplications([]);
//         setTotalPages(1);
//       }
//     } catch (err) {
//       console.error("Search error:", err);
//       setSearchError(err.response?.data?.message || 'Error performing search');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleResetSearch = async () => {
//     setSearchQuery('');
//     setSearchError(null);
    
//     try {
//       setLoading(true);
//       const response = await axios.get(`${apiUrl}/customers`);
//       let customers = response.data.customers || response.data;
//       customers = customers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
//       setApplications(customers);
//       setFilteredApplications(customers);
//       setTotalPages(Math.ceil(customers.length / itemsPerPage));
//       setCurrentPage(1);
//     } catch (err) {
//       console.error("Error resetting search:", err);
//       setError("Failed to reset search. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDelete = async (id) => {
//     if (window.confirm("Are you sure you want to delete this order?")) {
//       try {
//         await axios.delete(`${apiUrl}/customerdelete/${id}`);
//         setApplications(prev => prev.filter(app => app.id !== id));
//         setSelectedApplications(prev => prev.filter(appId => appId !== id));
//       } catch (err) {
//         console.error("Error deleting order:", err);
//         setError("Failed to delete order. Please try again.");
//       }
//     }
//   };

//   const handleEditClick = (app) => {
//     setEditingApplication(app.id);
//     setFormData({
//       order_id: app.order_id || '',
//       customer_name: app.customer_name || '',
//       customer_address: app.customer_address || '',
//       phone_number: app.phone_number || '',
//       product_name: app.product_name || '',
//       color: app.color || '',
//       size: app.size || '',
//       quantity: app.quantity || '',
//       total: app.total || '',
//       delivery_status: app.delivery_status || '',
//       delivery_note: app.delivery_note || '',
//     });
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({ ...prev, [name]: value }));
//   };

//   const handleAddPoints = async (customerId) => {
//     try {
//       const response = await axios.post(`${apiUrl}/customers/${customerId}/add-points`);
      
//       alert(`পয়েন্ট যোগ করা হয়েছে! যোগ হয়েছে: ${response.data.points_added}, মোট পয়েন্ট: ${response.data.total_points}`);
      
//       const customersRes = await axios.get(`${apiUrl}/customers`);
//       let customers = customersRes.data.customers || customersRes.data;
//       customers = customers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
//       setApplications(customers);
      
//     } catch (err) {
//       console.error("Error adding points:", err);
//       setError(err.response?.data?.message || "Failed to add points.");
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       await axios.put(`${apiUrl}/customersupdate/${editingApplication}`, formData);
//       setApplications(prev =>
//         prev.map(app =>
//           app.id === editingApplication ? { ...app, ...formData } : app
//         )
//       );
//       setEditingApplication(null);
//     } catch (err) {
//       console.error("Error updating order:", err);
//       setError(err.response?.data?.message || "Failed to update order.");
//     }
//   };

//   const handleCheckboxChange = (id) => {
//     setSelectedApplications(prev =>
//       prev.includes(id)
//         ? prev.filter(appId => appId !== id)
//         : [...prev, id]
//     );
//   };

//   const handleSelectAllChange = () => {
//     const currentItems = getCurrentPageItems();
//     if (selectAll) {
//       setSelectedApplications([]);
//     } else {
//       setSelectedApplications(currentItems.map(app => app.id));
//     }
//     setSelectAll(!selectAll);
//   };

//   const handleSubmitToSteadfast = async () => {
//     if (selectedApplications.length === 0) {
//       alert("অন্তত একটি অর্ডার সিলেক্ট করুন");
//       return;
//     }

//     const selectedOrders = applications
//       .filter(app => selectedApplications.includes(app.id))
//       .map(order => ({
//         invoice: order.order_id,
//         recipient_name: order.customer_name,
//         recipient_address: order.customer_address,
//         recipient_phone: order.phone_number,
//         cod_amount: Number(order.total),
//         note: order.delivery_note,
//       }));

//     try {
//       const response = await axios.post(
//         `${steadfastApiUrl}/create_order/bulk-order`, 
//         selectedOrders,
//         {
//           headers: {
//             'Api-Key': steadfast.apiKey,
//             'Secret-Key': steadfast.secretKey,
//             'Content-Type': 'application/json'
//           },
//           timeout: 30000
//         }
//       );

//       // Update database status regardless of response (since we know submission was attempted)
//       try {
//         // Update each selected order in the database
//         await Promise.all(selectedApplications.map(async (id) => {
//           const order = applications.find(app => app.id === id);
//           if (order) {
//             await axios.put(`${apiUrl}/customersupdate/${id}`, {
//               ...order,
//               delivery_status: 'confirmed'
//             });
//           }
//         }));

//         // Update local state
//         const updatedApplications = applications.map(app => {
//           if (selectedApplications.includes(app.id)) {
//             return {
//               ...app,
//               delivery_status: 'confirmed'
//             };
//           }
//           return app;
//         });

//         setApplications(updatedApplications);
        
//         // Update statuses in state
//         const newStatuses = {};
//         selectedApplications.forEach(id => {
//           const order = applications.find(app => app.id === id);
//           if (order) {
//             newStatuses[order.order_id] = 'confirmed';
//           }
//         });
        
//         setOrderStatuses(prev => ({ ...prev, ...newStatuses }));

//         alert("অর্ডার সফলভাবে সাবমিট হয়েছে এবং ডাটাবেস আপডেট করা হয়েছে!");

//       } catch (dbError) {
//         console.error("ডাটাবেস আপডেট করতে সমস্যা:", dbError);
//         alert("অর্ডার সাবমিট হয়েছে কিন্তু ডাটাবেস আপডেট করতে সমস্যা হয়েছে। পরে আবার চেষ্টা করুন।");
//       }

//     } catch (err) {
//       console.error("স্ট্যাডফাস্টে সাবমিট করার সময় এরর:", err);
//       let errorMessage = "অর্ডার সাবমিট করতে সমস্যা হয়েছে";
//       if (err.response) {
//         if (err.response.status === 401) {
//           errorMessage = "API Key বা Secret Key ভুল";
//         } else if (err.response.data?.message) {
//           errorMessage = err.response.data.message;
//         }
//       }
//       alert(errorMessage);
//     }
//   };

//   const getStatusColor = (status) => {
//     if (!status) return 'bg-gray-100 text-gray-800';
//     switch(status.toLowerCase()) {
//       case 'confirmed':
//         return 'bg-green-100 text-green-800';
//       case 'delivered':
//       case 'delivered_approval_pending':
//         return 'bg-green-100 text-green-800';
//       case 'cancelled':
//       case 'cancelled_approval_pending':
//         return 'bg-red-100 text-red-800';
//       case 'fraud':
//         return 'bg-red-100 text-red-800';
//       case 'error':
//         return 'bg-purple-100 text-purple-800';
//       default:
//         return 'bg-gray-100 text-gray-800';
//     }
//   };

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };

//   const Pagination = () => {
//     if (totalPages <= 1) return null;

//     const maxVisiblePages = 5;
//     let startPage, endPage;

//     if (totalPages <= maxVisiblePages) {
//       startPage = 1;
//       endPage = totalPages;
//     } else {
//       const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
//       const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;
      
//       if (currentPage <= maxVisibleBeforeCurrent) {
//         startPage = 1;
//         endPage = maxVisiblePages;
//       } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
//         startPage = totalPages - maxVisiblePages + 1;
//         endPage = totalPages;
//       } else {
//         startPage = currentPage - maxVisibleBeforeCurrent;
//         endPage = currentPage + maxVisibleAfterCurrent;
//       }
//     }

//     return (
//       <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
//         <div className="text-sm text-gray-700">
//           Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
//           <span className="font-medium">
//             {Math.min(currentPage * itemsPerPage, filteredApplications.length)}
//           </span>{' '}
//           of <span className="font-medium">{filteredApplications.length}</span> results
//         </div>
        
//         <div className="flex items-center gap-1">
//           <button
//             onClick={() => handlePageChange(currentPage - 1)}
//             disabled={currentPage === 1}
//             className="p-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
//             aria-label="Previous page"
//           >
//             <FaChevronLeft className="w-4 h-4" />
//           </button>
          
//           {startPage > 1 && (
//             <>
//               <button
//                 onClick={() => handlePageChange(1)}
//                 className={`px-3 py-1 border text-sm font-medium ${currentPage === 1 ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
//               >
//                 1
//               </button>
//               {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
//             </>
//           )}
          
//           {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(pageNum => (
//             <button
//               key={pageNum}
//               onClick={() => handlePageChange(pageNum)}
//               className={`px-3 py-1 border text-sm font-medium ${currentPage === pageNum ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
//             >
//               {pageNum}
//             </button>
//           ))}
          
//           {endPage < totalPages && (
//             <>
//               {endPage < totalPages - 1 && <span className="px-2 text-gray-500">...</span>}
//               <button
//                 onClick={() => handlePageChange(totalPages)}
//                 className={`px-3 py-1 border text-sm font-medium ${currentPage === totalPages ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
//               >
//                 {totalPages}
//               </button>
//             </>
//           )}
          
//           <button
//             onClick={() => handlePageChange(currentPage + 1)}
//             disabled={currentPage === totalPages}
//             className="p-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
//             aria-label="Next page"
//           >
//             <FaChevronRight className="w-4 h-4" />
//           </button>
//         </div>
//       </div>
//     );
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-64">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   const currentItems = getCurrentPageItems();

//   return (
//     <div className="p-4 md:p-6 max-w-7xl mx-auto">

//       {/* Product Details Modal */}
//       {showProductModal && selectedProductDetails && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
//             <div className="p-4 border-b flex justify-between items-center">
//               <h3 className="text-lg font-bold">Product Details</h3>
//               <button 
//                 onClick={() => setShowProductModal(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 <FaTimesCircle className="text-xl" />
//               </button>
//             </div>
//             <div className="p-6">
//               <div className="grid grid-cols-2 gap-4 mb-4">
//                 <div>
//                   <h4 className="text-sm font-medium text-gray-500">Product Name</h4>
//                   <p className="mt-1 text-sm text-gray-900">{selectedProductDetails.product_name}</p>
//                 </div>
//                 <div>
//                   <h4 className="text-sm font-medium text-gray-500">Color</h4>
//                   <p className="mt-1 text-sm text-gray-900">{selectedProductDetails.color || 'N/A'}</p>
//                 </div>
//                 <div>
//                   <h4 className="text-sm font-medium text-gray-500">Size</h4>
//                   <p className="mt-1 text-sm text-gray-900">{selectedProductDetails.size || 'N/A'}</p>
//                 </div>
//                 <div>
//                   <h4 className="text-sm font-medium text-gray-500">Quantity</h4>
//                   <p className="mt-1 text-sm text-gray-900">{selectedProductDetails.quantity}</p>
//                 </div>
//                 <div>
//                   <h4 className="text-sm font-medium text-gray-500">Price</h4>
//                   <p className="mt-1 text-sm text-gray-900">৳{selectedProductDetails.price}</p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Sales Report Modal */}
//       {showSalesReport && salesReport && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-auto">
//             <div className="p-4 border-b flex justify-between items-center">
//               <h3 className="text-lg font-bold">
//                 Sales Report: {salesReport.startDate} to {salesReport.endDate}
//               </h3>
//               <button 
//                 onClick={() => setShowSalesReport(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 <FaTimesCircle className="text-xl" />
//               </button>
//             </div>
//             <div className="p-6">
//               <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
//                 <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
//                   <h4 className="text-sm font-medium text-blue-700">Total Orders</h4>
//                   <p className="text-2xl font-bold text-blue-900">{salesReport.totalOrders}</p>
//                 </div>
//                 <div className="bg-green-50 p-4 rounded-lg border border-green-100">
//                   <h4 className="text-sm font-medium text-green-700">Total Sales</h4>
//                   <p className="text-2xl font-bold text-green-900">৳{salesReport.totalSales.toFixed(2)}</p>
//                 </div>
//                 <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
//                   <h4 className="text-sm font-medium text-purple-700">Delivered</h4>
//                   <p className="text-2xl font-bold text-purple-900">{salesReport.deliveredOrders}</p>
//                 </div>
//                 <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
//                   <h4 className="text-sm font-medium text-yellow-700">Pending</h4>
//                   <p className="text-2xl font-bold text-yellow-900">{salesReport.pendingOrders}</p>
//                 </div>
//               </div>

//               <div className="overflow-x-auto">
//                 <table className="min-w-full divide-y divide-gray-200">
//                   <thead className="bg-gray-50">
//                     <tr>
//                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
//                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
//                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
//                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
//                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
//                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
//                     </tr>
//                   </thead>
//                   <tbody className="bg-white divide-y divide-gray-200">
//                     {salesReport.orders.map((order) => (
//                       <tr key={order.id}>
//                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{order.order_id}</td>
//                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
//                           {new Date(order.created_at).toLocaleDateString()}
//                         </td>
//                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{order.customer_name}</td>
//                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{order.phone_number}</td>
//                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">৳{order.total}</td>
//                         <td className="px-4 py-3 whitespace-nowrap">
//                           <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(order.delivery_status)}`}>
//                             {order.delivery_status || 'Not Confirm'}
//                           </span>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>

//               <div className="mt-6 flex justify-end">
//                 <button
//                   onClick={() => {
//                     const csvContent = [
//                       ['Order ID', 'Date', 'Customer', 'Phone', 'Amount', 'Status'],
//                       ...salesReport.orders.map(order => [
//                         order.order_id,
//                         new Date(order.created_at).toLocaleDateString(),
//                         order.customer_name,
//                         order.phone_number,
//                         order.total,
//                         order.delivery_status || 'Not Confirm'
//                       ])
//                     ].map(e => e.join(",")).join("\n");

//                     const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
//                     const link = document.createElement("a");
//                     const url = URL.createObjectURL(blob);
//                     link.setAttribute("href", url);
//                     link.setAttribute("download", `sales_report_${salesReport.startDate}_to_${salesReport.endDate}.csv`);
//                     link.style.visibility = 'hidden';
//                     document.body.appendChild(link);
//                     link.click();
//                     document.body.removeChild(link);
//                   }}
//                   className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
//                 >
//                   Export to CSV
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="mb-6 bg-gray-50 p-4 rounded-lg">
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//           {/* Date Range Filter */}
//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
//             <div className="flex items-center gap-2">
//               <DatePicker
//                 selected={startDate}
//                 onChange={date => setStartDate(date)}
//                 selectsStart
//                 startDate={startDate}
//                 endDate={endDate}
//                 placeholderText="Start Date"
//                 className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//               />
//               <span className="text-gray-500">to</span>
//               <DatePicker
//                 selected={endDate}
//                 onChange={date => setEndDate(date)}
//                 selectsEnd
//                 startDate={startDate}
//                 endDate={endDate}
//                 minDate={startDate}
//                 placeholderText="End Date"
//                 className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//               />
//             </div>
//           </div>

//           <div className="flex items-end gap-2">
//             <button
//               onClick={applyDateFilter}
//               className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
//             >
//               Filter Orders
//             </button>
//             <button
//               onClick={generateSalesReport}
//               className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
//             >
//               Generate Report
//             </button>
//             {dateFilterApplied && (
//               <button
//                 onClick={resetDateFilter}
//                 className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
//               >
//                 Reset
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Search functionality */}
//         <div className="flex flex-col md:flex-row gap-4 items-end">
//           <div className="flex-1">
//             <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
//               Search Customers (Name, Phone, or Address)
//             </label>
//             <input
//               type="text"
//               id="search"
//               placeholder="Enter name, phone or address..."
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//               className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//             />
//           </div>
//           <div className="flex gap-2">
//             <button
//               onClick={handleSearch}
//               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
//             >
//               Search
//             </button>
//             <button
//               onClick={handleResetSearch}
//               className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
//             >
//               Reset
//             </button>
//           </div>
//         </div>
//         {searchError && (
//           <div className="mt-2 text-sm text-red-600">
//             {searchError}
//           </div>
//         )}
//       </div>

//       <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
//         <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
//           <h2 className="text-xl md:text-2xl font-bold text-gray-800">Customer Orders</h2>
//           <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
//             <button
//               onClick={refreshAllStatuses}
//               disabled={refreshLoading}
//               className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm md:text-base"
//             >
//               {refreshLoading ? (
//                 <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
//               ) : (
//                 <>
//                   <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
//                   </svg>
//                   Refresh Statuses
//                 </>
//               )}
//             </button>
//             <button
//               onClick={handleSubmitToSteadfast}
//               className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center text-sm md:text-base"
//               disabled={selectedApplications.length === 0}
//             >
//               <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
//               </svg>
//               Submit to Steadfast ({selectedApplications.length})
//             </button>
//           </div>
//         </div>

//         {/* Tab Navigation - Made scrollable for mobile */}
//         <div className="mb-6 border-b border-gray-200 overflow-x-auto">
//           <nav className="-mb-px flex space-x-4 whitespace-nowrap">
//             {['all', 'not_confirm', 'confirmed', 'delivered', 'return', 'cancelled', 'hold', 'blocked'].map(tab => (
//               <button
//                 key={tab}
//                 onClick={() => {
//                   setActiveTab(tab);
//                   setCurrentPage(1);
//                 }}
//                 className={`py-3 px-1 border-b-2 font-medium text-xs sm:text-sm ${activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
//               >
//                 {tab.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
//               </button>
//             ))}
//           </nav>
//         </div>

//         {error && (
//           <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg border border-red-100">
//             {error}
//             <button 
//               onClick={() => setError(null)} 
//               className="float-right text-red-800 font-bold"
//             >
//               ×
//             </button>
//           </div>
//         )}

//         <div className="overflow-x-auto w-full">
//           <table className="min-w-full divide-y divide-gray-200">
//             <thead className="bg-gray-50">
//               <tr>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   <input
//                     type="checkbox"
//                     checked={selectAll}
//                     onChange={handleSelectAllChange}
//                     className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
//                   />
//                 </th>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funnel Product</th>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cart Product</th>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extras</th>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
//                 <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
//               </tr>
//             </thead>
//             <tbody className="bg-white divide-y divide-gray-200">
//               {currentItems.map((app) => (
//                 <React.Fragment key={app.id}>
//                   <tr className="hover:bg-gray-50">
//                     <td className="px-2 py-4 whitespace-nowrap">
//                       <input
//                         type="checkbox"
//                         checked={selectedApplications.includes(app.id)}
//                         onChange={() => handleCheckboxChange(app.id)}
//                         className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
//                       />
//                     </td>
//                     <td className="px-2 py-4 whitespace-nowrap">
//                       <div className="flex flex-col">
//                         <span className="font-medium text-gray-900">{app.customer_name}</span>
//                         <span className="text-xs text-gray-500">{app.order_id}</span>
//                         <span className="text-xs text-gray-500">
//                           {new Date(app.created_at).toLocaleString('en-GB', {
//                             day: '2-digit',
//                             month: '2-digit',
//                             year: 'numeric',
//                             hour: '2-digit',
//                             minute: '2-digit',
//                             hour12: true,
//                           })}
//                         </span>
//                         <span className="text-sm text-gray-700">{app.phone_number}</span>
//                         {app.payment_number && app.transaction_id ?
//                         <>
//                         <span className="text-sm text-gray-700">{app.payment_method}: {app.payment_number}</span>
//                         <span className="text-sm text-gray-700">Tnx Id: { app.transaction_id}</span>
//                         </>: <p></p>
                             
//                         }
                     
//                         {app.delivery_status && (
//                           <div className="flex items-center mt-1">
//                             {app.delivery_status === 'delivered' && (
//                               <>
//                                 <FaCheckCircle className="text-green-500 text-xs mr-1" />
//                                 <span className="text-xs text-green-600 font-medium">Delivered</span>
//                               </>
//                             )}
//                             {app.delivery_status === 'cancelled' && (
//                               <>
//                                 <FaTimesCircle className="text-red-500 text-xs mr-1" />
//                                 <span className="text-xs text-red-600 font-medium">Cancelled</span>
//                               </>
//                             )}
//                             {app.delivery_status === 'return' && (
//                               <>
//                                 <FaUndo className="text-yellow-500 text-xs mr-1" />
//                                 <span className="text-xs text-yellow-600 font-medium">Return</span>
//                               </>
//                             )}
//                             {app.delivery_status === 'hold' && (
//                               <>
//                                 <FaPauseCircle className="text-blue-500 text-xs mr-1" />
//                                 <span className="text-xs text-blue-600 font-medium">Hold</span>
//                               </>
//                             )}
//                             {app.delivery_status === 'blocked' && (
//                               <>
//                                 <FaPauseCircle className="text-blue-500 text-xs mr-1" />
//                                 <span className="text-xs text-blue-600 font-medium">Blocked</span>
//                               </>
//                             )}
//                             {!['delivered', 'cancelled', 'return', 'hold', 'blocked'].includes(app.delivery_status) && (
//                               <>
//                                 <FaExclamationTriangle className="text-red-500 text-xs mr-1" />
//                                 <span className="text-xs text-red-600 font-medium">{app.delivery_status}</span>
//                               </>
//                             )}
//                           </div>
//                         )}


//                       </div>
//                     </td>
//                     <td className="px-2 py-4">
//                       <div className="text-sm text-gray-500">{app.customer_address}</div>
//                     </td>
//                     <td className="px-2 py-4">
//                       <div className="text-sm text-gray-900">{app.product_name}</div>
//                       <div className="text-xs text-gray-500">
//                         {[app.color, app.size, `Qty: ${app.quantity}`].filter(Boolean).join(' • ')}
//                       </div>
//                     </td>
//                     <td className="px-2 py-4">
//                       <div className="text-sm text-gray-900">
//                         <div className="flex items-center flex-wrap gap-2">
//                           {app.items && app.items.map((item) =>
//                             item.product_image && item.product_image.length > 0 ? (
//                               item.product_image.map((image, index) => (
//                                 <img
//                                   key={`${item.id}-${index}`}
//                                   src={`${imageUrl}/${image.image}`}
//                                   alt={`Product Image ${index + 1}`}
//                                   className="w-8 h-8 rounded-full cursor-pointer hover:opacity-75 transition-opacity"
//                                   onClick={() => handleImageClick(item)}
//                                 />
//                               ))
//                             ) : (
//                               item.colors && item.colors.length > 0 ? (
//                                 <img
//                                   key={`${item.id}`}
//                                   src={`${imageUrl}/${item.colors[0].image}`}
//                                   alt="Fallback Product Image"
//                                   className="w-8 h-8 rounded-full cursor-pointer hover:opacity-75 transition-opacity"
//                                   onClick={() => handleImageClick(item)}
//                                 />
//                               ) : (
//                                 <span key={item.id} className="text-xs text-gray-500">
//                                   No Image
//                                 </span>
//                               )
//                             )
//                           )}
//                         </div>
//                       </div>
//                     </td>
//                     <td className="px-2 py-4">
//                       {app.order_bumps?.length > 0 && (
//                         <div className="mt-1 text-xs">
//                           <div className="font-medium text-gray-600">Special Offers:</div>
//                           {app.order_bumps.map((bump, index) => (
//                             <div key={index} className="flex items-center">
//                               <FaPlusCircle className="text-blue-500 text-xs mr-1" />
//                               <span>{bump.bump?.title || `Additional Product #${index + 1}`}: ৳{bump.price}</span>
//                             </div>
//                           ))}
//                         </div>
//                       )}
//                       {app.bulk_discounts?.length > 0 && (
//                         <div className="mt-1 text-xs">
//                           <div className="font-medium text-gray-600">Combo Discounts:</div>
//                           {app.bulk_discounts.map((discount, index) => (
//                             <div key={index} className="flex items-center">
//                               <FaMinusCircle className="text-red-500 text-xs mr-1" />
//                               <span> -৳{Math.floor(app.product_price * discount.discount_percentage / 100)}</span>
//                             </div>
//                           ))}
//                         </div>
//                       )}
//                     </td>
//                     <td className="px-2 py-4 whitespace-nowrap">
//                       <div className="flex items-center">
//                         <button
//                           onClick={() => fetchStatusOnClick(app.id)}
//                           className="mr-2 text-blue-500 hover:text-blue-700"
//                           title="Check Status"
//                         >
//                           <FaEye className="text-sm" />
//                         </button>
//                         {showStatus[app.id] === 'loading' ? (
//                           <span className="text-xs text-gray-500">Loading...</span>
//                         ) : (
//                           <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(orderStatuses[app.order_id] || showStatus[app.id])}`}>
//                             {orderStatuses[app.order_id] || showStatus[app.id]}
//                           </span>
//                         )}
//                       </div>
//                     </td>
//                     <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
//                       ৳{app.total}
//                     </td>
//                     <td className="px-2 py-4 whitespace-nowrap space-x-1">
//                       <button
//                         onClick={() => handleEditClick(app)}
//                         className="text-blue-600 hover:text-blue-900 text-xs font-medium"
//                       >
//                         Edit
//                       </button>
//                       <button
//                         onClick={() => handlePrintClick(app)}
//                         className="text-purple-600 hover:text-purple-900 text-xs font-medium"
//                       >
//                         Print
//                       </button>
//                       <button
//                         onClick={() => handleDelete(app.id)}
//                         className="text-red-600 hover:text-red-900 text-xs font-medium"
//                       >
//                         Delete
//                       </button>
//                       {!app.delivery_status && activeTab === 'delivered' && (
//                         <button
//                           onClick={() => handleAddPoints(app.id)}
//                           className="text-green-600 hover:text-green-900 text-xs font-medium"
//                         >
//                           Points
//                         </button>
//                       )}
//                     </td>
//                   </tr>
                  
//                   {/* Status Action Buttons and Fraud Details Row */}
//                   <tr className="bg-gray-50">
//                     <td colSpan="9" className="px-4 py-4">
//                       <div className="space-y-4">
//                         {/* Status Action Buttons */}
//                         <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
//                           <button
//                             onClick={() => updateOrderStatus(app.id, 'delivered')}
//                             className="flex items-center justify-center gap-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg border border-green-100 hover:bg-green-100 transition-colors"
//                           >
//                             <FaCheck className="text-green-600" />
//                             <span className="text-sm font-medium">Delivered</span>
//                           </button>
//                           <button
//                             onClick={() => updateOrderStatus(app.id, 'cancelled')}
//                             className="flex items-center justify-center gap-2 bg-red-50 text-red-700 px-3 py-2 rounded-lg border border-red-100 hover:bg-red-100 transition-colors"
//                           >
//                             <FaExclamationTriangle className="text-red-600" />
//                             <span className="text-sm font-medium">Cancelled</span>
//                           </button>
//                           <button
//                             onClick={() => updateOrderStatus(app.id, 'return')}
//                             className="flex items-center justify-center gap-2 bg-yellow-50 text-yellow-700 px-3 py-2 rounded-lg border border-yellow-100 hover:bg-yellow-100 transition-colors"
//                           >
//                             <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
//                             </svg>
//                             <span className="text-sm font-medium">Return</span>
//                           </button>
//                           <button
//                             onClick={() => updateOrderStatus(app.id, 'hold')}
//                             className="flex items-center justify-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors"
//                           >
//                             <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
//                             </svg>
//                             <span className="text-sm font-medium">Hold</span>
//                           </button>
//                           <button
//                             onClick={() => updateOrderStatus(app.id, 'blocked')}
//                             className="flex items-center justify-center gap-2 bg-blue-50 text-red-700 px-3 py-2 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors"
//                           >
//                             <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
//                             </svg>
//                             <span className="text-sm font-medium">Blocked</span>
//                           </button>
//                         </div>

//                         {/* Fraud Check Section */}
//                         <div className="border-t pt-4">
//                           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
//                             <div className="flex items-center gap-3">
//                               <h3 className="text-sm font-medium text-gray-700">Fraud Verification</h3>
//                               <button
//                                 onClick={() => checkFraudDetails(app.phone_number)}
//                                 className="flex items-center gap-1 text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
//                               >
//                                 <FaExclamationTriangle />
//                                 Check Fraud Details
//                               </button>
//                             </div>
//                             {fraudDetails[app.phone_number]?.loading && (
//                               <span className="text-xs text-gray-500 flex items-center">
//                                 <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                                 </svg>
//                                 Loading...
//                               </span>
//                             )}
//                           </div>

//                           {fraudDetails[app.phone_number]?.data && (
//                             <div className="mt-3 p-4 bg-white border border-gray-200 rounded-lg shadow-xs">
//                               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                                 <div className="space-y-2">
//                                   <div className="flex justify-between">
//                                     <span className="text-sm font-medium text-gray-600">Total Parcels:</span>
//                                     <span className="text-sm text-gray-800">{fraudDetails[app.phone_number].data.total_parcels}</span>
//                                   </div>
//                                   <div className="flex justify-between">
//                                     <span className="text-sm font-medium text-gray-600">Total Delivered:</span>
//                                     <span className="text-sm text-gray-800">{fraudDetails[app.phone_number].data.total_delivered}</span>
//                                   </div>
//                                 </div>
//                                 <div className="space-y-2">
//                                   <div className="flex justify-between">
//                                     <span className="text-sm font-medium text-gray-600">Total Cancelled:</span>
//                                     <span className="text-sm text-gray-800">{fraudDetails[app.phone_number].data.total_cancelled}</span>
//                                   </div>
//                                   <div className="flex justify-between">
//                                     <span className="text-sm font-medium text-gray-600">Fraud Reports:</span>
//                                     <span className={`text-sm ${fraudDetails[app.phone_number].data.total_fraud_reports.length > 0 ? 'text-red-600 font-semibold' : 'text-green-600'}`}>
//                                       {fraudDetails[app.phone_number].data.total_fraud_reports.length > 0 ? (
//                                         `${fraudDetails[app.phone_number].data.total_fraud_reports.length} reports found`
//                                       ) : (
//                                         'No fraud reports'
//                                       )}
//                                     </span>
//                                   </div>
//                                 </div>
//                               </div>

//                               {fraudDetails[app.phone_number].data.total_fraud_reports.length > 0 && (
//                                 <div className="mt-4">
//                                   <h4 className="text-sm font-medium text-gray-700 mb-2">Report Details:</h4>
//                                   <div className="bg-red-50 border border-red-100 rounded-lg p-3">
//                                     <ul className="space-y-1.5 text-xs text-red-700">
//                                       {fraudDetails[app.phone_number].data.total_fraud_reports.map((report, index) => (
//                                         <li key={index} className="flex items-start">
//                                           <span className="inline-block mr-1.5 mt-0.5">•</span>
//                                           <span>{report}</span>
//                                         </li>
//                                       ))}
//                                     </ul>
//                                   </div>
//                                 </div>
//                               )}
//                             </div>
//                           )}

//                           {fraudDetails[app.phone_number]?.error && (
//                             <div className="mt-2 text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded">
//                               {fraudDetails[app.phone_number].error}
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </td>
//                   </tr>
//                 </React.Fragment>
//               ))}
//             </tbody>
//           </table>
//         </div>

//         {filteredApplications.length === 0 && !loading && (
//           <div className="text-center py-8 text-gray-500">
//             No orders found in this category.
//           </div>
//         )}

//         <Pagination />
//       </div>

//       {editingApplication && (
//         <UpdateCustomer
//           formData={formData}
//           handleChange={handleChange}
//           handleSubmit={handleSubmit}
//           loading={loading}
//           error={error}
//           setEditingApplication={setEditingApplication}
//         />
//       )}

//       {showPrintPreview && printingInvoice && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-auto">
//             <div className="p-4 border-b flex justify-between items-center">
//               <h3 className="text-lg font-bold">Invoice Preview</h3>
//               <div className="space-x-2">
//                 <button 
//                   onClick={handlePrint}
//                   className="bg-blue-600 text-white px-4 py-2 rounded"
//                 >
//                   Print
//                 </button>
//                 <button 
//                   onClick={() => setShowPrintPreview(false)}
//                   className="bg-gray-600 text-white px-4 py-2 rounded"
//                 >
//                   Close
//                 </button>
//               </div>
//             </div>
//             <div className="p-6">
//               <InvoiceTemplate
//                 ref={invoiceRef} 
//                 order={printingInvoice} 
//                 logoUrl="/images/logo.png" 
//               />
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ViewCustomer;